import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, TrendingUp as TrendingUpIcon, Crosshair, Film, Brain, ClipboardList, ArrowRight, LogOut, Users, Calendar, ArrowUpRight, GraduationCap,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isRestricted } from "../contexts/AuthContext";
import { visibleTabs } from "../lib/permissions";
import Navbar from "../components/Navbar";
import PackageBadge from "../components/PackageBadge";
import BasketballIcon from "../components/BasketballIcon";
import PlayerBadges from "../components/PlayerBadges";
import { BookSessionButton } from "../components/sessions/BookSession";
import { ArchetypeRoleCard } from "../components/inventory/ArchetypeRoleCard";
import { NextGameCard } from "../components/NextGameCard";

const GOLD = "#d8b854";
const BLUE = "#3aa0ff";
const initials = (name) => (name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
const fmtDate = (iso, opts = { month: "short", day: "numeric" }) => {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-US", opts);
};
const n0 = (v) => (v === null || v === undefined ? "—" : Math.round(v));
const n1 = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(1));
const pct = (v) => (v === null || v === undefined ? "—" : `${(Number(v) * 100).toFixed(1)}%`);
const ageFrom = (iso) => {
  if (!iso) return null;
  const b = new Date(iso); if (isNaN(b)) return null;
  const now = new Date(); let a = now.getFullYear() - b.getFullYear();
  if (now < new Date(now.getFullYear(), b.getMonth(), b.getDate())) a -= 1;
  return a;
};
const fmtHeight = (h) => {
  if (!h) return null;
  const m = String(h).match(/^(\d)\s*[-'’]\s*(\d{1,2})/);
  return m ? { ft: m[1], inch: m[2] } : { raw: String(h) };
};

const ADVANCED_VIEWERS = ["steve@maximizebasketball.com", "justin@maximizebasketball.com"];

// "The Newman School" → "Newman", "IMG Academy" → "IMG", "Detroit Pistons" → "Pistons"
const GENERIC_WORDS = new Set(["the", "school", "academy", "high", "hs", "prep", "preparatory", "catholic", "regional", "senior", "of"]);
export function watermarkWord(name) {
  const words = String(name || "").split(/\s+/).filter(Boolean);
  const core = words.filter((w) => !GENERIC_WORDS.has(w.toLowerCase().replace(/[^a-z]/g, "")));
  const pick = core.length ? core : words;
  const joined = pick.join(" ");
  return joined.length <= 14 ? joined : pick[pick.length - 1] || "";
}

function buildTiles(home, user) {
  const p = home.player;
  const isPlatinum = (p.training_level || "").toLowerCase() === "the program platinum";
  const isHS = p.league === "HS";
  const hideAdvanced = isHS || (isPlatinum && !ADVANCED_VIEWERS.includes((user?.email || "").toLowerCase()));
  const lg = home.analytics?.last_game;
  const tr = home.training || {};
  const iq = home.iq || {};
  const film = home.film || {};
  const sd = home.shot_diet;
  const inv = home.inventory;
  const iqPct = iq.assigned ? Math.round((iq.watched / iq.assigned) * 100) : null;
  const tiles = [
    { key: "analytics", label: "Analytics", Icon: TrendingUpIcon, accent: BLUE,
      headline: lg ? `${n0(lg.pts)} PTS · ${n0(lg.reb)} REB · ${n0(lg.ast)} AST` : "No games logged yet",
      sub: lg ? `Last game ${fmtDate(lg.date)}${lg.matchup ? ` · ${lg.matchup}` : ""}` : "Game logs will show here once the season starts",
      meta: home.analytics?.games_played ? `${home.analytics.games_played} games` : null },
    { key: "training", label: "Training", Icon: BasketballIcon, accent: GOLD,
      headline: `${tr.routines || 0} ${tr.routines === 1 ? "routine" : "routines"}`,
      sub: tr.last_workout ? `Last workout ${fmtDate(tr.last_workout.date)}${tr.last_workout.focus_areas?.length ? ` · ${tr.last_workout.focus_areas.slice(0, 2).join(", ")}` : ""}` : "No workouts logged yet",
      meta: tr.workouts ? `${tr.workouts} workouts` : null },
    { key: "iq", label: "IQ", Icon: Brain, accent: "#c084fc",
      headline: iq.assigned ? `${iqPct}% complete` : "No courses assigned",
      sub: iq.assigned ? `${iq.assigned - iq.watched} of ${iq.assigned} videos left to watch` : "Your coach will assign film study here",
      meta: iq.courses ? `${iq.courses} ${iq.courses === 1 ? "course" : "courses"}` : null, progress: iqPct },
    { key: "film", label: "Film Reviews", Icon: Film, accent: "#4ade80",
      headline: film.latest ? film.latest.title : "No film reviews yet",
      sub: film.latest ? `Reviewed ${fmtDate(film.latest.date)}` : "Coach breakdowns of your games land here",
      meta: film.count ? `${film.count} ${film.count === 1 ? "review" : "reviews"}` : null },
  ];
  if (isHS) {
    const rc = home.recruitment || {};
    tiles.push({ key: "recruitment", label: "Recruitment", Icon: GraduationCap, accent: "#f87171",
      headline: rc.committed ? `Committed · ${rc.committed}` : rc.schools ? `${rc.schools} ${rc.schools === 1 ? "school" : "schools"}${rc.offers ? ` · ${rc.offers} ${rc.offers === 1 ? "offer" : "offers"}` : ""}` : "No schools tracked yet",
      sub: rc.latest ? `Latest: ${rc.latest.college_name} · ${rc.latest.interest_level}${rc.latest.date ? ` · ${fmtDate(rc.latest.date)}` : ""}` : "College interest, offers and contacts live here",
      meta: rc.offers ? `${rc.offers} ${rc.offers === 1 ? "offer" : "offers"}` : null, clamp: true });
  }
  if (!hideAdvanced) {
    tiles.push({ key: "shot_diet", label: "Shot Diet", Icon: Crosshair, accent: "#f87171",
      headline: sd?.top_growth ? sd.top_growth.label : "No shot analysis yet",
      sub: sd?.top_growth ? sd.top_growth.sentence : "Shot selection breakdown will appear here",
      meta: sd ? `Top growth · ${fmtDate(sd.created_at)}` : null, clamp: true });
  }
  tiles.push({ key: "inventory", label: "Development Inventory", Icon: ClipboardList, accent: "#a0a0a0",
    headline: inv ? inv.period : "Not started yet", sub: inv ? `Updated ${fmtDate(inv.updated_at)}` : "Coach's full development read on your game" });
  return tiles;
}

function Tile({ t, onOpen, index }) {
  const { Icon } = t;
  return (
    <button onClick={onOpen}
      className="group relative text-left border border-white/10 bg-[#141414] hover:border-white/30 p-5 sm:p-6 flex flex-col gap-4 min-h-[170px] overflow-hidden"
      style={{ transition: "border-color .2s ease", animation: "homeIn .5s ease both", animationDelay: `${index * 60}ms` }}
      data-testid={`home-tile-${t.key}`}>
      <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: t.accent }} />
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 flex items-center justify-center border border-white/10 bg-[#101010]" style={{ color: t.accent }}><Icon className="w-4 h-4" /></span>
          <span className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#a0a0a0] whitespace-nowrap">{t.label}</span>
        </div>
        {t.meta && <span className="text-[10px] uppercase tracking-[0.2em] text-[#575757] shrink-0 truncate max-w-[45%]" data-testid={`home-tile-${t.key}-meta`}>{t.meta}</span>}
      </div>
      <div className="flex-1">
        <div className={`font-heading uppercase text-white leading-none ${t.clamp ? "text-2xl" : "text-3xl sm:text-[2.1rem]"}`} data-testid={`home-tile-${t.key}-headline`}>{t.headline}</div>
        <div className={`text-[12px] text-[#a0a0a0] mt-2 leading-relaxed ${t.clamp ? "line-clamp-2" : ""}`}>{t.sub}</div>
        {t.progress !== null && t.progress !== undefined && (
          <div className="mt-3 h-1 bg-white/10 w-full"><div className="h-full" style={{ width: `${t.progress}%`, background: t.accent }} /></div>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: t.accent }}>
        Open <ArrowRight className="w-3 h-3 group-hover:translate-x-1" style={{ transition: "transform .2s ease" }} />
      </div>
    </button>
  );
}

function Measure({ label, value, unit, sub, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[9px] uppercase tracking-[0.35em] text-[#757575] mb-1">{label}</div>
      <div className="font-heading text-3xl sm:text-4xl text-white leading-none">
        {value}{unit && <span className="text-base text-[#a0a0a0] ml-1 font-sans">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-[#575757] mt-0.5">{sub}</div>}
    </div>
  );
}

function LastGameCard({ lg, team }) {
  const res = lg?.result || "";
  const win = res.startsWith("W");
  const scores = res.match(/(\d+)\s*-\s*(\d+)/);
  return (
    <div className="bg-[#141414] border border-white/10 relative" style={{ animation: "homeIn .6s ease both", animationDelay: "120ms" }} data-testid="home-last-game">
      <div className="flex items-center border-b border-white/10">
        <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.3em] font-bold text-[#121212]" style={{ background: GOLD }}>Last Game</div>
        {lg?.date && <div className="px-4 text-[10px] uppercase tracking-[0.25em] text-[#757575] flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(lg.date, { month: "short", day: "numeric", year: "numeric" })}</div>}
      </div>
      {lg ? (
        <div className="p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">{lg.is_home ? "Home" : lg.is_home === false ? "Away" : "Matchup"}</div>
              <div className="font-heading text-2xl text-white uppercase truncate">{lg.team_abbr || team}</div>
            </div>
            <div className="text-center shrink-0">
              {scores ? (
                <div className="font-heading text-4xl sm:text-5xl leading-none text-white">
                  {scores[1]}<span className="text-[#575757] mx-2">–</span>{scores[2]}
                </div>
              ) : <div className="font-heading text-3xl text-white">{lg.matchup}</div>}
              {res && <div className={`text-[10px] uppercase tracking-[0.3em] font-bold mt-1 ${win ? "text-[#4ade80]" : "text-[#f87171]"}`}>{win ? "Final · Win" : res.startsWith("L") ? "Final · Loss" : res}</div>}
            </div>
            <div className="min-w-0 text-right">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">Opponent</div>
              <div className="font-heading text-2xl text-white uppercase truncate">{lg.opponent || (lg.matchup || "").replace(/^(vs|@)\s*/, "")}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-white/10">
            {[["PTS", lg.pts], ["REB", lg.reb], ["AST", lg.ast]].map(([k, v]) => (
              <div key={k} className="text-center">
                <div className="font-heading text-3xl text-white leading-none">{n0(v)}</div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mt-1">{k}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-6 text-[12px] text-[#757575]">No games logged yet — this card fills in once the season starts.</div>
      )}
    </div>
  );
}

function StatBlock({ label, value, dark, index }) {
  return (
    <div className="relative overflow-hidden text-center py-6 px-4" data-testid={`home-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
      style={{ background: dark ? "#0e1a2b" : `linear-gradient(160deg, ${BLUE} 0%, #1d63b8 100%)`, animation: "homeIn .6s ease both", animationDelay: `${200 + index * 80}ms` }}>
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/85 pb-3 border-b border-white/15">{label}</div>
      <div className="font-heading text-5xl sm:text-6xl text-white leading-none mt-5 flex items-start justify-center gap-1">
        {value}<ArrowUpRight className="w-4 h-4 text-white/70 mt-1" />
      </div>
    </div>
  );
}

function BioRow({ k, v, testId }) {
  if (v === null || v === undefined || v === "") return null;
  return (
    <div className="flex items-center justify-between gap-6 py-2.5 border-b border-white/10" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#757575]">{k}</span>
      <span className="text-sm text-white text-right">{v}</span>
    </div>
  );
}

export default function PlayerHome() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [home, setHome] = useState(null);
  const [error, setError] = useState(null);
  const [bust] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    setHome(null);
    api.playerHome(id).then((d) => alive && setHome(d)).catch((e) => alive && setError(e?.response?.data?.detail || "Could not load"));
    return () => { alive = false; };
  }, [id]);

  if (error) return <div className="min-h-screen bg-[#0a0a0a] text-white"><Navbar /><div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#a0a0a0]" data-testid="home-error">{error}</div></div>;
  if (!home) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--org-accent)]" /></div>;

  const p = home.player;
  const bio = p.bio || {};
  const sa = home.analytics?.season_averages;
  const lg = home.analytics?.last_game;
  const tiles = buildTiles(home, user).filter(t => visibleTabs(user, p.league).includes(t.key));
  const goTab = (tab) => navigate(`/player/${p.id}?tab=${tab}`);
  const restricted = isRestricted(user);
  const h = fmtHeight(p.height);
  const age = ageFrom(bio.birthdate);
  const from = [bio.city, bio.state].filter(Boolean).join(", ") || bio.hometown || p.country || null;
  const teamWord = watermarkWord((p.league === "HS" && bio.hs_name) || p.team);
  const nameParts = (p.name || "").split(" ");
  const hasBioLeft = bio.birthdate || from || bio.hs_name || bio.aau_program;
  const hasBioRight = bio.grad_year || bio.class_year || p.college || p.draft_year || p.training_level || bio.gpa;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="player-home">
      <style>{`@keyframes homeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }`}</style>
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-white/10" style={{ background: "linear-gradient(180deg, #121212 0%, #0a0a0a 100%)" }}>
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          <div className="absolute left-1/2 top-6 -translate-x-1/2 font-heading uppercase whitespace-nowrap text-white/[0.035] leading-none" style={{ fontSize: "clamp(6rem, 22vw, 20rem)" }}>{teamWord}</div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.06]" style={{ width: 620, height: 620 }} />
          <div className="absolute inset-0 opacity-[0.10]" style={{ backgroundImage: `radial-gradient(circle at 15% 25%, ${GOLD} 0, transparent 38%), radial-gradient(circle at 85% 80%, ${BLUE} 0, transparent 42%)` }} />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-10 pt-8 sm:pt-12 pb-8 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-6 items-end">
          {/* Left — identity */}
          <div className="order-2 lg:order-1 self-center" style={{ animation: "homeIn .6s ease both" }}>
            <div className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }} data-testid="home-welcome">Welcome back{nameParts[0] ? `, ${nameParts[0]}` : ""}</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9] text-white" data-testid="home-player-name">
              {nameParts.slice(0, -1).join(" ")}<br />{nameParts.slice(-1)[0]}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-5">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-1 border" style={{ color: BLUE, borderColor: `${BLUE}55` }}>{p.league}</span>
              {p.team && <span className="text-base text-white" data-testid="home-player-team">{p.team}</span>}
              {p.position && <span className="text-sm text-[#a0a0a0]">· {p.position}</span>}
              {p.league === "HS" && bio.package_name && <PackageBadge name={bio.package_name} status={bio.package_status} testId="home-package" />}
            </div>
            <div className="grid grid-cols-3 gap-6 mt-8 max-w-sm">
              {h && <Measure label="Height" testId="home-height" value={h.ft ? <>{h.ft}<span className="text-base text-[#a0a0a0] font-sans mx-1">ft</span>{h.inch}</> : h.raw} unit={h.ft ? "in" : null} />}
              {p.weight && <Measure label="Weight" testId="home-weight" value={String(p.weight).replace(/\D/g, "") || p.weight} unit="lbs" />}
              {(bio.grad_year || age) && <Measure label={bio.grad_year ? "Class" : "Age"} testId="home-class" value={bio.grad_year || age} />}
            </div>
            <div className="mt-8 lg:max-w-md"><ArchetypeRoleCard playerId={p.id} snapshot={home.inventory} archetypes={home.archetypes} /></div>
          </div>

          {/* Center — headshot + jersey number */}
          <div className="order-1 lg:order-2 relative flex justify-center items-end" style={{ animation: "homeIn .7s ease both" }}>
            {p.jersey_number && (
              <div className="relative z-10 font-heading leading-none select-none -mr-5 sm:-mr-8 mb-6" style={{ fontSize: "clamp(5rem, 10vw, 9.5rem)", color: GOLD, textShadow: "0 8px 30px rgba(0,0,0,.6)" }} data-testid="home-jersey">
                <span className="text-[0.4em] align-top mr-0.5">#</span>{p.jersey_number}
              </div>
            )}
            <div className="relative w-[220px] h-[275px] sm:w-[280px] sm:h-[350px] border-2 bg-[#141414] overflow-hidden shrink-0" style={{ borderColor: `${GOLD}99`, boxShadow: "0 30px 60px -20px rgba(0,0,0,.8)" }} data-testid="home-headshot">
              <div className="absolute inset-0 flex items-center justify-center font-heading text-7xl" style={{ color: GOLD }}>{initials(p.name)}</div>
              {p.has_headshot && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/players/${p.id}/headshot?v=${bust}`} alt={p.name}
                  className="relative w-full h-full object-cover object-top" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
              <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(180deg, transparent, rgba(10,10,10,.9))" }} />
              {p.position && <div className="absolute bottom-3 right-3 font-heading text-3xl" style={{ color: GOLD }}>{p.position}</div>}
            </div>
          </div>

          {/* Right — last game + actions */}
          <div className="order-3 flex flex-col gap-4 self-center">
            <div className="flex items-center gap-2 justify-end flex-wrap">
              <BookSessionButton playerId={p.id} playerName={p.name} accent={GOLD} />
              <button onClick={() => navigate(`/player/${p.id}`)} className="flex items-center gap-2 px-3 py-2 border text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[#3aa0ff]/10" style={{ color: BLUE, borderColor: `${BLUE}55` }} data-testid="home-full-profile">
                Full Profile <ArrowRight className="w-3 h-3" />
              </button>
              {restricted && (
                <button onClick={() => navigate("/my-players")} className="flex items-center gap-2 px-3 py-2 border border-white/15 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white" data-testid="home-switch-player">
                  <Users className="w-3 h-3" /> Players
                </button>
              )}
              {restricted && (
                <button onClick={logout} className="flex items-center gap-2 px-3 py-2 border border-white/15 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white" data-testid="home-logout">
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              )}
            </div>
            <NextGameCard playerId={p.id} league={p.league} />
            <LastGameCard lg={lg} team={p.team} />
          </div>
        </div>

        {/* Season stat blocks */}
        <div className="relative max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-10 pb-10">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#757575]">Season averages</div>
            {sa?.games_played ? <div className="text-[10px] uppercase tracking-[0.3em] text-[#575757]" data-testid="home-gp">{sa.games_played} GP</div> : null}
          </div>
          {sa ? (
            <>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <StatBlock label="Points Per Game" value={n1(sa.pts)} index={0} />
                <StatBlock label="Rebounds Per Game" value={n1(sa.reb)} dark index={1} />
                <StatBlock label="Assists Per Game" value={n1(sa.ast)} index={2} />
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-px bg-white/10 mt-3 border border-white/10" data-testid="home-splits">
                {[["FG%", pct(sa.fg_pct)], ["3P%", pct(sa.fg3_pct)], ["FT%", pct(sa.ft_pct)], ["STL", n1(sa.stl)], ["BLK", n1(sa.blk)], ["MIN", sa.min ?? "—"]].map(([k, v]) => (
                  <div key={k} className="bg-[#141414] px-4 py-3 flex items-baseline justify-between gap-2">
                    <span className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">{k}</span>
                    <span className="font-heading text-xl text-white">{v}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="border border-dashed border-white/10 py-8 text-center text-[12px] text-[#757575]" data-testid="home-no-stats">Season stats will appear here once games are logged.</div>
          )}
          <PlayerBadges playerId={p.id} initial={p.badges || []} className="mt-5" />
        </div>
      </section>

      {/* ============ BIO ROWS ============ */}
      {(hasBioLeft || hasBioRight) && (
        <section className="max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-10 pt-10 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2" data-testid="home-bio">
          <div>
            <BioRow k="Born" v={bio.birthdate ? `${fmtDate(bio.birthdate, { month: "2-digit", day: "2-digit", year: "numeric" })}${age ? ` · ${age} yrs` : ""}` : null} testId="home-bio-born" />
            <BioRow k="From" v={from} testId="home-bio-from" />
            <BioRow k="High School" v={bio.hs_name} testId="home-bio-hs" />
            <BioRow k="AAU" v={bio.aau_program} testId="home-bio-aau" />
          </div>
          <div>
            <BioRow k="Class of" v={bio.grad_year} testId="home-bio-class" />
            <BioRow k="Year" v={bio.class_year} testId="home-bio-class-year" />
            <BioRow k="College" v={p.college} testId="home-bio-college" />
            <BioRow k="Draft" v={p.draft_year} testId="home-bio-draft" />
            <BioRow k="Program" v={[p.training_level, p.training_sub_tier].filter(Boolean).join(" · ")} testId="home-bio-program" />
            <BioRow k="GPA" v={bio.gpa} testId="home-bio-gpa" />
          </div>
        </section>
      )}

      {/* ============ SECTION TILES ============ */}
      <section className="max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-10 py-10 sm:py-12">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#757575] mb-5">Your development hub</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-tiles">
          {tiles.map((t, i) => <Tile key={t.key} t={t} index={i} onOpen={() => goTab(t.key)} />)}
        </div>
      </section>
    </div>
  );
}
